import React from 'react';

import { ArrowLeft, Phone, Globe, Mail, MapPin, UserPlus, MessageCircle } from 'lucide-react';
import type { Trader, TraderFeatures } from '../services/traderService';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';

// MockupBadge removed - no longer showing demo badges
import SocialLinksList from './SocialLinksList';
import FeatureShowcase from './FeatureShowcase';

interface TraderDetailProps {
  trader: Trader & { trader_features?: TraderFeatures };
  onBack: () => void;
}

export default function TraderDetail({ trader, onBack }: TraderDetailProps) {
  // Demo notification removed
  const navigate = useNavigate();

  // Check if this is an unclaimed business (mockup=true and no user_id)
  const isUnclaimedBusiness = trader.mockup && !trader.user_id;

  // Check if email is a fake/placeholder email (used for unclaimed businesses without real contact)
  const isFakeEmail = trader.email?.endsWith('@unclaimed.ballymarket.local') || false;
  
  // Check if email is a Google domain (gmail.com, googlemail.com)
  const isGoogleEmail = trader.email?.toLowerCase().endsWith('@gmail.com') || 
                        trader.email?.toLowerCase().endsWith('@googlemail.com') || false;
  
  // Determine if we should show the real email (not fake)
  const hasRealEmail = trader.email && !isFakeEmail;

  const normalizedPhoneNumber = trader.phone_number?.trim() || '';
  const hasRealPhoneNumber = Boolean(normalizedPhoneNumber && normalizedPhoneNumber !== '1234567890');
  
  const handleClaimBusiness = () => {
    navigate(`/claim/${trader.id}`);
  };

  const enabledFeatures = React.useMemo(() => {
    console.log('Full Trader in TraderDetail:', trader);
    console.log('Full Trader Features:', trader.trader_features);

    if (!trader.trader_features) {
      console.warn('No trader features found for trader:', trader.store_name);
      return [];
    }

    const traderFeatures = trader.trader_features || {};

    console.log('Trader Features Structure:', Object.keys(traderFeatures));

    const features = [
      {
        title: traderFeatures.feature1_title || 'Feature 1',
        description: traderFeatures.feature1_description || '',
        image: traderFeatures.feature1_image || trader.avatar_url || '/bally-Market-sign-200px.png',
        enabled: traderFeatures.feature1_enabled
      },
      {
        title: traderFeatures.feature2_title || 'Feature 2',
        description: traderFeatures.feature2_description || '',
        image: traderFeatures.feature2_image || trader.avatar_url || '/bally-Market-sign-200px.png',
        enabled: traderFeatures.feature2_enabled
      },
      {
        title: traderFeatures.feature3_title || 'Feature 3',
        description: traderFeatures.feature3_description || '',
        image: traderFeatures.feature3_image || trader.avatar_url || '/bally-Market-sign-200px.png',
        enabled: traderFeatures.feature3_enabled
      }
    ].filter(feature => {
      const enabledValue = feature.enabled as any;
      const isEnabled =
        enabledValue === true ||
        enabledValue === 'true' ||
        enabledValue === '1' ||
        enabledValue === 1;

      return isEnabled;
    });

    console.log('Filtered Features:', features);
    return features;
  }, [trader.trader_features]);

  const hasLocation = Boolean(trader.location && trader.location.trim().length > 0);
  const shouldShowMap = (trader as any).show_map !== false;
  const mapUrl = hasLocation && shouldShowMap
    ? `https://www.google.com/maps?q=${encodeURIComponent(`${trader.location}, Ireland`)}&output=embed`
    : null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 mt-16"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Traders
        </button>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-6 space-y-8">
            <div className="flex flex-col gap-8 md:grid md:grid-cols-2 md:gap-10">
              <section className="space-y-3 md:col-span-2">
                <div className="flex items-start gap-3">
                  <h1 className="text-2xl font-bold text-gray-900 flex-1">
                    {trader.store_name}
                  </h1>
                </div>
                {trader.profession?.category && (
                  <p className="text-sm uppercase tracking-wide text-gray-500">
                    {trader.profession.category}
                  </p>
                )}
              </section>

              {/* Image / Avatar */}
              <section className="md:col-span-1">
                <div className="w-full max-w-sm mx-auto aspect-square rounded-lg overflow-hidden bg-gray-100">
                  {trader.avatar_url ? (
                    <img
                      src={trader.avatar_url}
                      alt={trader.store_name}
                      className="w-full h-full object-cover object-center"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                      No image available
                    </div>
                  )}
                </div>
              </section>

              {/* Contact Details */}
              <section className="space-y-3 md:col-span-1">
                <p className="text-gray-700">
                  <span className="font-medium">Owner:</span> {trader.owner_name}
                </p>

                {/* Only show email if it's a real email, not a placeholder */}
                {hasRealEmail && (
                  <p className="flex items-center text-gray-700">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Email:</span>
                    <a href={`mailto:${trader.email}`} className="text-blue-600 hover:text-blue-800">
                      {trader.email}
                    </a>
                  </p>
                )}

                {hasRealPhoneNumber && (
                  <p className="flex items-center text-gray-700">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Phone:</span>
                    <a href={`tel:${normalizedPhoneNumber}`} className="text-blue-600 hover:text-blue-800">
                      {normalizedPhoneNumber}
                    </a>
                  </p>
                )}

                {trader.website && (
                  <p className="flex items-center text-gray-700">
                    <Globe className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Website:</span>
                    <a 
                      href={trader.website.startsWith('http') ? trader.website : `https://${trader.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 truncate"
                    >
                      {trader.website}
                    </a>
                  </p>
                )}

                <SocialLinksList userId={trader.user_id} className="mt-3" />

                <p className="flex items-center text-gray-700">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="font-medium mr-2">Location:</span>
                  {trader.location}
                </p>

                {trader.profession && typeof trader.profession === 'object' && trader.profession.name && (
                  <p className="text-gray-700">
                    <span className="font-medium">Profession:</span> {trader.profession.name}
                  </p>
                )}
              </section>
            </div>

            {/* About Section */}
            <section className="pt-4 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {trader.description || 'No description available.'}
              </p>
            </section>

            {/* Featured Offerings Section */}
            {enabledFeatures.length > 0 && (
              <section className="pt-4 border-t border-gray-200">
                <FeatureShowcase
                  title={
                    (trader.trader_features as TraderFeatures | undefined)?.features_section_title ||
                    'Featured Offerings'
                  }
                  features={enabledFeatures}
                  layout={
                    (trader.trader_features as TraderFeatures | undefined)?.features_layout ||
                    'rows'
                  }
                />
              </section>
            )}

            {/* Map Section */}
            {mapUrl && (
              <section className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Location Map</h3>
                <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                  <iframe
                    title={`Map for ${trader.store_name}`}
                    src={mapUrl}
                    width="100%"
                    height="260"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </section>
            )}

            {/* Claim Your Business Section - Only show for unclaimed businesses */}
            {isUnclaimedBusiness && (
              <section className="pt-4 border-t border-gray-200">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 text-center">
                  {isFakeEmail ? (
                    // Business has no real email - show contact us message
                    <>
                      <MessageCircle className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Is this your business?
                      </h2>
                      <p className="text-gray-600 mb-4 max-w-md mx-auto">
                        We don't have contact details for this business on file. 
                        If you're the owner, please contact us to verify your ownership and claim this listing.
                      </p>
                      <a
                        href="mailto:info@ballymarket.ie?subject=Business%20Claim%20Request%20-%20" 
                        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Mail className="w-5 h-5 mr-2" />
                        Contact Us to Claim
                      </a>
                      <p className="text-sm text-gray-500 mt-3">
                        Email us at <strong>info@ballymarket.ie</strong> with proof of ownership
                      </p>
                    </>
                  ) : (
                    // Business has real email - show claim button
                    <>
                      <UserPlus className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Is this your business?
                      </h2>
                      <p className="text-gray-600 mb-4 max-w-md mx-auto">
                        Claim this listing to get full access to your business profile. 
                        You'll be able to update your information, add photos, and manage your presence on BallyMarket.
                      </p>
                      <button
                        onClick={handleClaimBusiness}
                        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <UserPlus className="w-5 h-5 mr-2" />
                        Claim Your Business
                      </button>
                      {isGoogleEmail && (
                        <p className="text-sm text-gray-500 mt-3">
                          You can sign up with your Google account ({trader.email})
                        </p>
                      )}
                    </>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}